import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { getSource } from '@/sources/registry';
import { parseCookies } from '@/lib/cookies';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAccountIdentity, useAccountToken } from '@/features/account';
import {
  restoreAccountSyncedSourceAuthToLocal,
  saveAccountSyncedSourceAuth,
} from '@/features/account/accountSourceAuth';
import { linkTwinSourceAuth } from '@/features/auth/twinSourceAuth';
import { useSourceAuthLoader } from '@/features/auth/useSourceAuthLoader';
import { SadPanda } from '@/lib/errors';
import {
  deleteSourceLoginSecret,
  loadSourceLoginSecret,
  saveSourceLoginSecret,
} from '@/db/sourceLoginSecrets';
import { connectErrorMessage } from './connectAccountErrors';
import { loginWithFormAndSave, validateCookieSave } from './connectAccountSave';
import type { SourceAuth, SourceCookies } from '@/sources/types';

/** E-Hentai login cookies that ExHentai's session bootstrap accepts. */
// `igneous` is deliberately excluded: it belongs to exhentai.org, and a stale
// copy would stop Bunko from fetching a fresh one.
const EH_LOGIN_COOKIES = ['ipb_member_id', 'ipb_pass_hash', 'sk'] as const;

function cookiePlaceholder(sourceId: string, anonymousMode: boolean): string {
  if (anonymousMode) return 'cf_clearance=...; __cf_bm=...';
  if (sourceId === 'exhentai') {
    return 'ipb_member_id=...; ipb_pass_hash=...; igneous=... (optional); sk=...';
  }
  if (sourceId === 'ehentai') {
    return 'ipb_member_id=...; ipb_pass_hash=...; cf_clearance=...; sk=...';
  }
  return 'name=value; another=value';
}

/**
 * Generic cookie-paste UX. Each source can refine the instructions via
 * `capabilities.loginNotes` (rendered below the textarea). The source
 * workers can also override this page later if they need richer UX.
 */
export function ConnectAccountPage() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const getAccountToken = useAccountToken();
  const loadSourceAuth = useSourceAuthLoader({ deriveTwin: false });
  const accountIdentity = useAccountIdentity();
  const currentAccountIdentity = useRef(accountIdentity);
  currentAccountIdentity.current = accountIdentity;
  const source = sourceId ? getSource(sourceId) : undefined;
  const [accountReady, setAccountReady] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberLogin, setRememberLogin] = useState(true);
  const [raw, setRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // "Browse anonymously" mode: the user pastes only Cloudflare
  // bot-detection cookies (cf_clearance / __cf_bm) so the worker's
  // egress IP can clear the destination CF challenge. We skip the
  // logged-in check on `validateAuth`, persist `auth.anonymous=true`,
  // and `useLibrary` then skips server-side favorites refresh for
  // this source. See `docs/source-quirks.md` for the underlying CF
  // egress-IP issue this works around.
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [skipSourceValidation, setSkipSourceValidation] = useState(false);
  const supportsRememberLogin = Boolean(source?.capabilities.rememberLogin);

  // Prefill the username of a login remembered on this device so an expired
  // session only needs the password (or nothing, once auto re-login works).
  useEffect(() => {
    if (!sourceId || !supportsRememberLogin) return;
    let cancelled = false;
    void loadSourceLoginSecret(sourceId).then((saved) => {
      if (!cancelled && saved) setUsername((current) => current || saved.username);
    });
    return () => {
      cancelled = true;
    };
  }, [sourceId, supportsRememberLogin]);

  useEffect(() => {
    let cancelled = false;
    setAccountReady(false);
    getAccountToken()
      .then((token) => {
        if (!cancelled) setAccountReady(Boolean(token));
      })
      .catch(() => {
        if (!cancelled) setAccountReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getAccountToken]);

  useEffect(
    () => () => {
      currentAccountIdentity.current = undefined;
    },
    [],
  );

  if (!source) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Unknown source</h1>
        <Link to="/" className="text-sm underline">
          ← Back
        </Link>
      </div>
    );
  }

  if (!source.capabilities.login) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">{source.name}</h1>
        <p className="text-sm text-muted-foreground">This source does not require login.</p>
      </div>
    );
  }

  async function handleRestoreFromAccount() {
    if (!source) return;
    const token = await getAccountToken();
    if (!token || !accountIdentity) {
      toast.error('Sign in before restoring source cookies.');
      return;
    }
    setRestoring(true);
    try {
      const restored = await restoreAccountSyncedSourceAuthToLocal(
        source.id,
        token,
        accountIdentity,
        () => currentAccountIdentity.current,
      );
      if (!restored) {
        toast.error(`No synced cookies found for ${source.name}.`);
        return;
      }
      toast.success(`Restored cookies for ${source.name} from account sync.`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Restore failed: ${msg}`);
    } finally {
      setRestoring(false);
    }
  }

  async function saveCookies(cookies: SourceCookies, opts: { anonymous: boolean }) {
    if (!source) return false;
    const token = await getAccountToken();
    if (!token || !accountIdentity) {
      toast.error('Sign in before saving source cookies.');
      return false;
    }
    setBusy(true);
    try {
      const pasted: SourceAuth = opts.anonymous ? { cookies, anonymous: true } : { cookies };
      const validation = await validateCookieSave(source, pasted, {
        anonymousMode: opts.anonymous,
        skipSourceValidation,
      });
      await saveAccountSyncedSourceAuth(
        source.id,
        validation.auth,
        token,
        accountIdentity,
        () => currentAccountIdentity.current,
        {
          mode: opts.anonymous ? 'anonymous' : undefined,
          username: validation.username,
        },
      );
      toast.success(
        skipSourceValidation
          ? `Saved cookies for ${source.name} without validating them. Reader metadata still uses the proxy, so retry after any upstream block clears.`
          : opts.anonymous
            ? `Saved anonymous CF cookies for ${source.name} to account sync.`
            : `Saved cookies for ${source.name} to account sync.`,
      );
      if (!opts.anonymous) await linkTwin(validation.auth, token, accountIdentity);
      return true;
    } catch (err) {
      toast.error(`Failed: ${connectErrorMessage(err, source)}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** E-Hentai and ExHentai share one login; give the twin the same one. */
  async function linkTwin(auth: SourceAuth, token: string, identity: string) {
    if (!source) return;
    const result = await linkTwinSourceAuth(source.id, auth, {
      getSource,
      loadAuth: loadSourceAuth,
      saveAuth: (sourceId, twinAuth) =>
        saveAccountSyncedSourceAuth(
          sourceId,
          twinAuth,
          token,
          identity,
          () => currentAccountIdentity.current,
        ),
    });
    if (result.status === 'not-applicable' || result.status === 'unchanged') return;
    const twin = getSource(result.sourceId);
    const twinName = twin?.name ?? result.sourceId;
    if (result.status === 'linked') {
      toast.success(`Also connected ${twinName} with the same login.`);
    } else if (result.error instanceof SadPanda) {
      toast.info(
        `${twinName} isn't available for this account yet (Sad Panda), so only ${source.name} was connected.`,
      );
    } else {
      toast.warning(
        `Couldn't link ${twinName}: ${twin ? connectErrorMessage(result.error, twin) : String(result.error)} Reconnect ${twinName} to retry.`,
      );
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    const cookies = parseCookies(raw);
    if (Object.keys(cookies).length === 0) {
      toast.error('No cookies parsed. Paste a `Cookie:` header or JSON dict.');
      return;
    }
    if (await saveCookies(cookies, { anonymous: anonymousMode })) setRaw('');
  }

  async function handleUseEhentaiLogin() {
    const ehentai = getSource('ehentai');
    const ehAuth = ehentai ? await loadSourceAuth(ehentai) : undefined;
    const cookies: SourceCookies = {};
    for (const name of EH_LOGIN_COOKIES) {
      const value = ehAuth?.cookies?.[name];
      if (value) cookies[name] = value;
    }
    if (ehAuth?.anonymous || !cookies.ipb_member_id || !cookies.ipb_pass_hash) {
      toast.error(
        'No logged-in E-Hentai connection found. Connect E-Hentai with ipb_member_id and ipb_pass_hash first, or paste them here.',
      );
      return;
    }
    await saveCookies(cookies, { anonymous: false });
  }

  async function handleFormLogin(e: FormEvent) {
    e.preventDefault();
    if (!source) return;
    const token = await getAccountToken();
    if (!token || !accountIdentity) {
      toast.error('Sign in before connecting a source account.');
      return;
    }
    setBusy(true);
    try {
      await loginWithFormAndSave(
        source,
        { username: username.trim(), password },
        token,
        (sourceId, auth, saveToken, opts) =>
          saveAccountSyncedSourceAuth(
            sourceId,
            auth,
            saveToken,
            accountIdentity,
            () => currentAccountIdentity.current,
            opts,
          ),
      );
      if (supportsRememberLogin) {
        await (rememberLogin
          ? saveSourceLoginSecret(source.id, { username: username.trim(), password })
          : deleteSourceLoginSecret(source.id)
        ).catch(() => toast.error('Connected, but could not update the remembered password.'));
      }
      toast.success(`Connected ${source.name} to account sync.`);
      setPassword('');
    } catch (err) {
      toast.error(`Failed: ${connectErrorMessage(err, source)}`);
    } finally {
      setBusy(false);
    }
  }

  const supportsFormLogin = Boolean(source.loginWithForm);

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold">Connect {source.name}</h1>
        <p className="text-sm text-muted-foreground">
          {supportsFormLogin
            ? 'Sign in with this source once. Bunko sends the credentials to the source API, then stores only the returned plugin token state in account sync.'
            : 'Paste one Cookie header line from your browser. Bunko normally checks the cookies with the source before saving; if the proxy is temporarily blocked, use the save-without-validation checkbox below.'}
        </p>
      </header>

      {!accountReady && (
        <Card className="border-blue-500/40 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Sign in required</CardTitle>
            <CardDescription>
              Sign in before saving source cookies. This keeps cookies tied to your Bunko account
              and avoids local-only passphrases.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/account">
              <Button variant="outline">Open Bunko account</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {accountReady && (
        <Card className="border-blue-500/40 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Already connected on another device?
            </CardTitle>
            <CardDescription>
              Restore synced cookies from your Bunko account instead of pasting them again on this
              device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={() => void handleRestoreFromAccount()}
              disabled={restoring}
            >
              {restoring ? 'Restoring…' : 'Restore from account sync'}
            </Button>
          </CardContent>
        </Card>
      )}

      {accountReady && source.id === 'exhentai' && (
        <Card className="border-blue-500/40 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Logged into E-Hentai already?</CardTitle>
            <CardDescription>
              Reuse your saved E-Hentai login. Bunko asks exhentai.org for the igneous cookie on
              your behalf, so you don&apos;t have to copy cookies from exhentai.org.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => void handleUseEhentaiLogin()} disabled={busy}>
              {busy ? 'Connecting…' : 'Use my E-Hentai login'}
            </Button>
          </CardContent>
        </Card>
      )}

      {!supportsFormLogin && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardHeader className="flex flex-row items-start gap-3 space-y-0">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="space-y-1">
              <CardTitle className="text-sm font-medium">
                Just want to browse anonymously?
              </CardTitle>
              <CardDescription>
                Hosted deploys talk to {source.name} from the proxy&apos;s egress path, which some
                sites challenge even when they still work in your normal browser. Tick the box below
                to paste only Cloudflare clearance cookies as a best-effort workaround (favorites
                stay disabled).
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      {supportsFormLogin ? (
        <Card>
          <CardHeader>
            <CardTitle>Source account login</CardTitle>
            <CardDescription>
              {supportsRememberLogin
                ? 'Bunko syncs only the returned login token. If you remember the password, it stays encrypted on this device so Bunko can log in again when the token expires.'
                : 'Bunko does not store the password after this request completes.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormLogin} className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span>Username</span>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  disabled={busy}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={busy}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              {supportsRememberLogin && (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rememberLogin}
                    onChange={(e) => setRememberLogin(e.target.checked)}
                    disabled={busy}
                    className="mt-0.5 h-4 w-4 cursor-pointer"
                  />
                  <span className="flex flex-col gap-1">
                    <span>Remember login on this device</span>
                    <span className="text-xs text-muted-foreground">
                      Lets Bunko renew the login automatically when it expires. Stored encrypted in
                      this browser only and never synced to your Bunko account.
                    </span>
                  </span>
                </label>
              )}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={
                    busy || username.trim().length === 0 || password.length === 0 || !accountReady
                  }
                >
                  {busy ? 'Connecting…' : 'Connect'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Paste cookies</CardTitle>
            <CardDescription>
              Accepts a raw <code>Cookie:</code> header line or a JSON dict.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="flex flex-col gap-3">
              <Textarea
                rows={6}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={cookiePlaceholder(source.id, anonymousMode)}
                disabled={busy}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={anonymousMode}
                  onChange={(e) => setAnonymousMode(e.target.checked)}
                  disabled={busy}
                  className="h-4 w-4 cursor-pointer"
                />
                <span>Browse anonymously (Cloudflare cookies only — favorites disabled)</span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipSourceValidation}
                  onChange={(e) => setSkipSourceValidation(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-4 w-4 cursor-pointer"
                />
                <span className="flex flex-col gap-1">
                  <span>Save without validating against {source.name}</span>
                  <span className="text-xs text-muted-foreground">
                    Use this when the proxy is temporarily rate-limited. Saving skips the source
                    check, but Bunko still uses the proxy for gallery pages and e-hentai API calls
                    later.
                  </span>
                </span>
              </label>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy || raw.length === 0 || !accountReady}>
                  {busy
                    ? 'Saving…'
                    : skipSourceValidation
                      ? 'Save without validation'
                      : anonymousMode
                        ? 'Save anonymous'
                        : 'Save'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {source.capabilities.loginNotes && (
        <Card>
          <CardHeader>
            <CardTitle>Instructions for {source.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{source.capabilities.loginNotes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
